import React, { memo, useMemo } from 'react';
import { CalculationEngineMonthlyValueOverrides, RunCalculationEngineResponse } from '../../../../../Generated/Raven-Demeter';
import { IChartDataSeries } from '../../../../Components/Charts/ChartDefinitions';
import ChartWrapper from '../../../../Components/Charts/ChartWrapper/ChartWrapper';
import StackedBarLineChartRaw from '../../../../Components/Charts/StackedBarLine/StackedBarLineChartRaw';
import formattingService from '../../../../Services/Formatting/FormattingService';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { MarginCompositeModel } from '../../RiskDefinitions';

export interface IProductionVersusHedgedVolumeChart {
    runCalculationEngineResponse: RunCalculationEngineResponse;
    getFarmerMarginCalculatorCompositeRowsModels: MarginCompositeModel[];
    runMonthlyValueOverrides: Array<CalculationEngineMonthlyValueOverrides> | null;
    getCalculatorFieldValue: (marginCompositeModel: MarginCompositeModel, fieldName: keyof MarginCompositeModel) => number;
    isLoading: boolean;
    displayDecimalPlaces: number;
}

const outputHedgedVolumeKeys = ['hedgedClass3Volume', 'hedgedClass4Volume', 'hedgedOutputVolume'];

const ProductionVersusHedgedVolumeChart: React.FC<IProductionVersusHedgedVolumeChart> = (props: IProductionVersusHedgedVolumeChart) => {
    const [translations] = useLanguage();

    const getHedgedVolumeValue = (marginCompositeModel: MarginCompositeModel) => {
        const currentValueOverrides = props.runMonthlyValueOverrides?.find(
            (x) => x.asOfDate === formattingService.toApiDate(marginCompositeModel.asOfDate),
        )?.valueOverrides;

        const keysOfCurrentRunMonthlyValueOverrides = Object.keys(currentValueOverrides ?? {});

        if (keysOfCurrentRunMonthlyValueOverrides.includes('hedgedOutputVolume')) {
            return currentValueOverrides?.hedgedOutputVolume ?? 0;
        }

        return currentValueOverrides!.hedgedClass3Volume ?? currentValueOverrides!.hedgedClass4Volume;
    };

    const someSubValuesAreMissing = () => {
        const keysOfValueOverrides = props.runMonthlyValueOverrides?.flatMap((x) => Object.keys(x.valueOverrides));
        return keysOfValueOverrides?.some((x) => outputHedgedVolumeKeys.includes(x));
    };

    const combineVolumeFields = (marginCompositeModel: MarginCompositeModel, field: string) => {
        const fieldsToCombine = marginCompositeModel.outputHedgedVolume.children
            ?.flatMap((x) => {
                if (x.children) {
                    return x.children;
                }

                return x;
            })
            .filter((x) => x!.variableName.includes('Volume') && x!.variableName.includes(field));

        const finalValue = fieldsToCombine
            ?.map((x) => props.getCalculatorFieldValue(marginCompositeModel, x!.variableName as keyof MarginCompositeModel))
            .reduce((a, b) => a + b);

        return finalValue ?? 0;
    };

    const runCalculatorEngineLines = props.runCalculationEngineResponse.results![0].lines;

    const stackedBarSeries = useMemo<IChartDataSeries[]>(() => {
        const currentValueOverridesIncludesOtherValues = someSubValuesAreMissing();

        const options = [
            {
                label: translations.risk.margin.text.dairyRevenueProtection,
                data: props.getFarmerMarginCalculatorCompositeRowsModels!.map((x) => ({
                    value: combineVolumeFields(x, 'insurance'),
                    asOfDate: new Date(x.asOfDate),
                    isActualValue: true,
                    percentValue: props.getCalculatorFieldValue(x, x.outputHedgedPercent.variableName as keyof MarginCompositeModel),
                })),
                stack: 'insuranceFuturesOptionsPhysical',
            },
            {
                label: translations.risk.margin.text.futures,
                data: props.getFarmerMarginCalculatorCompositeRowsModels!.map((x) => ({
                    value: combineVolumeFields(x, 'futures'),
                    asOfDate: new Date(x.asOfDate),
                    isActualValue: true,
                })),
                stack: 'insuranceFuturesOptionsPhysical',
            },
            {
                label: translations.risk.margin.text.options,
                data: props.getFarmerMarginCalculatorCompositeRowsModels!.map((x) => ({
                    value: combineVolumeFields(x, 'options'),
                    asOfDate: new Date(x.asOfDate),
                    isActualValue: true,
                })),
                stack: 'insuranceFuturesOptionsPhysical',
            },
            {
                label: translations.risk.margin.text.physicallyContracted,
                data: props.getFarmerMarginCalculatorCompositeRowsModels!.map((x) => ({
                    value: combineVolumeFields(x, 'physical'),
                    asOfDate: new Date(x.asOfDate),
                    isActualValue: true,
                })),
                stack: 'insuranceFuturesOptionsPhysical',
            },
            {
                label: runCalculatorEngineLines.find((x) => x.variableName === 'production')!.displayName,
                data: props.getFarmerMarginCalculatorCompositeRowsModels!.map((x) => ({
                    value: props.getCalculatorFieldValue(x, 'production' as keyof MarginCompositeModel),
                    asOfDate: new Date(x.asOfDate),
                    isActualValue: true,
                })),
            },
        ];

        return options;
    }, [props.getFarmerMarginCalculatorCompositeRowsModels, props.runCalculationEngineResponse]);

    const linesSeries = useMemo<IChartDataSeries[]>(
        () => [
            {
                label: props.getFarmerMarginCalculatorCompositeRowsModels[0].futuresBlendPrice.displayName,
                data: props.getFarmerMarginCalculatorCompositeRowsModels!.map((x) => ({
                    value: props.getCalculatorFieldValue(x, x.futuresBlendPrice.variableName as keyof MarginCompositeModel)!,
                    asOfDate: new Date(x.asOfDate),
                    isActualValue: true,
                })),
            },
            {
                label: props.getFarmerMarginCalculatorCompositeRowsModels[0].outputHedgedPrice.displayName,
                data: props.getFarmerMarginCalculatorCompositeRowsModels!.map((x) => ({
                    value: props.getCalculatorFieldValue(x, x.outputHedgedPrice.variableName as keyof MarginCompositeModel)!,
                    asOfDate: new Date(x.asOfDate),
                    isActualValue: true,
                })),
            },
        ],
        [props.getFarmerMarginCalculatorCompositeRowsModels, props.runCalculationEngineResponse],
    );

    const dataSourceTag = useMemo(() => translations.dataSource.StoneXCalculations, [translations]);

    return (
        <ChartWrapper
            name="ProductionVersusHedgedVolumeChart"
            title={translations.risk.headers.productionVersusHedgedVolume}
            dataSourceTag={dataSourceTag}
            isLoading={props.getFarmerMarginCalculatorCompositeRowsModels.length === 0 || props.isLoading}
            track={{ eventCategory: 'MarginCalculator', eventAction: 'ProductionVersusHedgedVolumeChart' }}
        >
            <StackedBarLineChartRaw
                barsSeries={stackedBarSeries}
                linesSeries={linesSeries}
                unitOfMeasure={props.getFarmerMarginCalculatorCompositeRowsModels[0]?.production.unitOfMeasure}
                currency={props.getFarmerMarginCalculatorCompositeRowsModels[0]?.production.currency}
                displayDecimalPlacesMaximum={props.displayDecimalPlaces}
                displayDecimalPlacesMinimum={props.displayDecimalPlaces}
            />
        </ChartWrapper>
    );
};

export default memo(ProductionVersusHedgedVolumeChart);
